'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { closest, elementHasClass, moveFocusToNext } from '../utils/Dom';
import { uniqueId } from '../utils/underscore';
import passthroughProps from '../utils/propTypes/passthroughProps';
import Controllable from '../decorators/Controllable';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { cloneElement, Children, PureComponent } from 'react';
import { nodeDisplayNameMatches } from '../core/ReactNodes';
import SyntheticEvent from '../core/SyntheticEvent';
import { hidden } from '../utils/propTypes/decorators';
import UIAbstractDropdown from './abstract/UIAbstractDropdown';
import UIAbstractDropdownWithSearchbox from './abstract/UIAbstractDropdownWithSearchbox';
import UIPopover from '../tooltip/UIPopover';
import { wrapPropTypes } from '../utils/propTypes/wrapPropTypes';
import { DropdownContextProvider } from '../context/DropdownContext';
import { FloatingButtonGroupContext } from '../context/FloatingButtonGroupContext';
const USE_CLASSES = {
  list: 'private-dropdown--list',
  unstyled: ''
};

class UIDropdown extends PureComponent {
  static getDerivedStateFromProps(props, state) {
    // Only autofocus when `open` prop goes from false => true
    if (!state.prevOpen && props.open) {
      return {
        enableAutofocus: true,
        prevOpen: props.open
      };
    }

    return null;
  }

  constructor(props) {
    super(props);

    this.menuRefCallback = ref => {
      this._menuEl = ref;
      if (ref) this.checkForSearchbox();
    };

    this.handleOpenComplete = () => {
      const {
        onOpenComplete,
        use
      } = this.props;

      if (!(this.state.hasSearchbox || use === 'unstyled')) {
        // Move the focus to the first menu item (<li>).
        this.focusNextMenuItem();
      }

      if (onOpenComplete) onOpenComplete();
    };

    this.handleMenuClick = evt => {
      const {
        context: {
          setFloatingButtonGroupOpen
        },
        props: {
          closeOnMenuClick,
          onOpenChange
        }
      } = this;

      if (closeOnMenuClick) {
        const {
          target
        } = evt;
        let iterableTarget = target;
        if (!closest(target, '[data-dropdown-menu]')) return; // Portals! #6595

        while (iterableTarget && !elementHasClass(iterableTarget, 'private-dropdown') && iterableTarget.getAttribute('data-component-name') !== 'UIFileButton') {
          // Only close when clicking a `button` or `a` tag!
          if ((['BUTTON', 'A'].includes(iterableTarget.nodeName) || iterableTarget.getAttribute('data-uic-allow-dropdown-close') === 'true') && !iterableTarget.disabled) {
            onOpenChange(SyntheticEvent(false));

            if (setFloatingButtonGroupOpen) {
              setFloatingButtonGroupOpen(false);
            }

            return;
          }

          iterableTarget = iterableTarget.parentElement;
        }
      }
    };

    this.handleKeyDown = evt => {
      const {
        key
      } = evt;
      const {
        onOpenChange,
        open,
        use
      } = this.props;
      if (!open) return;

      if (this.state.hasSearchbox) {
        if (key === 'Enter') {
          onOpenChange(SyntheticEvent(false));
        }

        evt.stopPropagation();
        return;
      } // Move the focus to the next/prev menu item (<li>).


      if (use !== 'unstyled' && (key === 'ArrowDown' || key === 'ArrowUp')) {
        if (this.focusNextMenuItem(key === 'ArrowUp')) {
          evt.preventDefault();
        }

        evt.stopPropagation();
      }
    };

    this.renderContent = () => {
      const {
        children,
        Content
      } = this.props;
      const {
        enableAutofocus,
        hasSearchbox
      } = this.state;
      let mutatedChildren = children;

      if (hasSearchbox && !Content) {
        mutatedChildren = Children.map(children, child => {
          if (nodeDisplayNameMatches(child, /UITypeahead|UIGroupedTypeahead/)) {
            return /*#__PURE__*/cloneElement(child, {
              autoFocus: enableAutofocus ? true : child.props.autoFocus
            });
          }

          return child;
        });
      }

      return /*#__PURE__*/_jsx("div", {
        "data-dropdown-menu": true,
        id: this._id,
        onClick: this.handleMenuClick,
        ref: this.menuRefCallback,
        role: "presentation",
        children: Content ? /*#__PURE__*/_jsx(Content, {}) : mutatedChildren
      });
    };

    this.state = {
      enableAutofocus: false,
      hasSearchbox: false,
      prevOpen: props.open
    };
    this._id = `dropdown-menu-${uniqueId()}`;
    this._dropdownCtxValue = {
      shouldLogModalWarning: true
    };
  }

  componentDidUpdate() {
    const {
      props: {
        open: isDropdownOpen
      },
      context: {
        setFloatingButtonGroupOpen,
        isFloatingButtonGroupOpen
      }
    } = this;
    this.checkForSearchbox();

    if (setFloatingButtonGroupOpen && isDropdownOpen && !isFloatingButtonGroupOpen) {
      this.props.onOpenChange(SyntheticEvent(false));
      setFloatingButtonGroupOpen(false);
    }
  }

  checkForSearchbox() {
    // Check if we have a searchbox, as we may need to make the arrow color match.
    if (this._menuEl && document.body.contains(this._menuEl)) {
      const hasSearchbox = !!this._menuEl.querySelector('.private-typeahead:not(.hide-search)');

      if (this.state.hasSearchbox !== hasSearchbox) {
        this.setState({
          hasSearchbox
        });
      }
    }
  }

  focusNextMenuItem(reverse = false) {
    const menu = document.getElementById(this._id);
    return moveFocusToNext('li', menu, reverse);
  }

  render() {
    const _this$props = this.props,
          {
      buttonClassName,
      buttonText,
      buttonUse,
      children,
      className,
      dropdownClassName,
      locked,
      iconName,
      popoverProps,
      use
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["buttonClassName", "buttonText", "buttonUse", "children", "className", "closeOnMenuClick", "Content", "dropdownClassName", "locked", "iconName", "popoverProps", "use"]);

    const containsFooter = Children.toArray(children).some(child => nodeDisplayNameMatches(child, /UIDropdownFooter/));
    const computedDropdownClassName = classNames(className, dropdownClassName, USE_CLASSES[use], containsFooter && 'p-y-0');
    const defaultIcon = buttonText ? undefined : 'settings';
    return /*#__PURE__*/_jsx(DropdownContextProvider, {
      value: this._dropdownCtxValue,
      children: /*#__PURE__*/_jsx(UIAbstractDropdownWithSearchbox, Object.assign({}, rest, {
        buttonText: buttonText,
        buttonUse: buttonUse,
        className: buttonClassName,
        Content: this.renderContent,
        dropdownClassName: computedDropdownClassName,
        hasSearchbox: this.state.hasSearchbox,
        iconName: iconName !== undefined ? iconName : defaultIcon,
        locked: locked,
        onOpenComplete: this.handleOpenComplete,
        onKeyDown: this.handleKeyDown,
        popoverProps: popoverProps,
        children: children
      }))
    });
  }

}

UIDropdown.propTypes = Object.assign({}, wrapPropTypes(UIAbstractDropdown), {
  arrowColor: hidden(UIAbstractDropdown.propTypes.arrowColor),

  /**
   * A custom className that is passed to the button that opens the dropdown
   * menu.
   */
  buttonClassName: PropTypes.string,

  /**
   * Whether the dropdown menu automatically closes itself when an action button
   * is clicked or selected with the keyboard.
   */
  closeOnMenuClick: PropTypes.bool.isRequired,

  /**
   * Sets the initial open state of the dropdown menu. Use `defaultOpen`
   * instead of `open` if you want the dropdown to be uncontrolled.
   */
  defaultOpen: PropTypes.bool,
  popoverProps: passthroughProps(UIPopover),

  /**
   * If `use="list"`, the dropdown menu is given automatic styles, such as
   * padding and style overrides to the child action buttons. If
   * `use="unstyled"`, the dropdown menu does not get those stylings.
   */
  use: PropTypes.oneOf(Object.keys(USE_CLASSES)).isRequired
});
UIDropdown.defaultProps = Object.assign({}, UIAbstractDropdown.defaultProps, {
  buttonUse: 'tertiary-light',
  closeOnMenuClick: true,
  menuWidth: 'auto',
  pinToConstraint: true,
  use: 'list'
});
UIDropdown.displayName = 'UIDropdown';
UIDropdown.contextType = FloatingButtonGroupContext;
export default Controllable(UIDropdown, ['open']);